import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { format } from "date-fns";
import { useCreateReminder } from "@/hooks/useReminders";
import type { CreateReminderRequest, ReminderPreset, RecurrenceRule } from "@shared/reminders";
import { PRESET_LABELS } from "@shared/reminders";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaults?: Partial<CreateReminderRequest>;
  userTimezone: string; // assignee's TZ to display preview
}

// Mirror of resolvePreset for client-side preview only. Uses browser locale TZ approximation.
function previewPreset(preset: ReminderPreset, tz: string): Date {
  const now = new Date();
  if (preset === "tomorrow_9am") {
    const d = new Date(now.toLocaleString("en-US", { timeZone: tz }));
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return d;
  }
  if (preset === "next_monday_9am") {
    const d = new Date(now.toLocaleString("en-US", { timeZone: tz }));
    const dow = d.getDay();
    const days = dow === 1 ? 7 : (8 - dow) % 7 || 7;
    d.setDate(d.getDate() + days);
    d.setHours(9, 0, 0, 0);
    return d;
  }
  // first_of_next_month_9am
  const d = new Date(now.toLocaleString("en-US", { timeZone: tz }));
  return new Date(d.getFullYear(), d.getMonth() + 1, 1, 9, 0, 0, 0);
}

export function ReminderPicker({ open, onOpenChange, defaults, userTimezone }: Props) {
  const [title, setTitle] = useState(defaults?.title ?? "");
  const [note, setNote] = useState(defaults?.note ?? "");
  const [preset, setPreset] = useState<ReminderPreset | "custom">("tomorrow_9am");
  const [customDateTime, setCustomDateTime] = useState<string>("");
  const [assignedToId, setAssignedToId] = useState<string | undefined>(undefined); // undefined = me
  const [recurrenceRule, setRecurrenceRule] = useState<RecurrenceRule | "none">("none");
  const [recurrenceEndDate, setRecurrenceEndDate] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const create = useCreateReminder();

  const usersQ = useQuery<Array<{ id: string; name: string; email: string }>>({
    queryKey: ["users", "for-assignment"],
    queryFn: () => fetch("/api/users", { credentials: "include" }).then(r => r.json()),
    staleTime: 5 * 60_000,
  });

  const presetPreviewLabel = useMemo(() => {
    if (preset === "custom") return null;
    const d = previewPreset(preset, userTimezone);
    return format(d, "EEE MMM d, h:mm a");
  }, [preset, userTimezone]);

  async function handleSubmit() {
    if (!title.trim()) return;
    setSubmitting(true);
    try {
      const body: CreateReminderRequest = {
        title: title.trim(),
        note: note.trim() || undefined,
        entityType: defaults?.entityType ?? null,
        entityId: defaults?.entityId ?? null,
        emailMicrosoftId: defaults?.emailMicrosoftId,
        emailWebLink: defaults?.emailWebLink,
        emailSubject: defaults?.emailSubject,
        emailFrom: defaults?.emailFrom,
        recurrenceRule: recurrenceRule === "none" ? null : recurrenceRule,
        recurrenceEndDate: recurrenceEndDate || null,
      };
      if (preset === "custom") body.customDateTime = customDateTime;
      else body.preset = preset;
      if (assignedToId) body.assignedToId = assignedToId;
      await create.mutateAsync(body);
      onOpenChange(false);
      // Reset
      setTitle(""); setNote(""); setPreset("tomorrow_9am"); setCustomDateTime("");
      setAssignedToId(undefined);
      setRecurrenceRule("none"); setRecurrenceEndDate("");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Set a reminder</DialogTitle></DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="What should we remind you about?" />
          </div>

          <div>
            <Label>When</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {(["tomorrow_9am", "next_monday_9am", "first_of_next_month_9am"] as ReminderPreset[]).map(p => (
                <Button key={p} type="button" size="sm" variant={preset === p ? "default" : "outline"} onClick={() => setPreset(p)}>
                  {PRESET_LABELS[p].replace(" 9 AM", "")}
                </Button>
              ))}
            </div>
            <Button type="button" size="sm" variant={preset === "custom" ? "default" : "ghost"} className="mt-2" onClick={() => setPreset("custom")}>
              Custom date & time
            </Button>
            {preset === "custom" && (
              <Input type="datetime-local" className="mt-2" value={customDateTime} onChange={e => setCustomDateTime(e.target.value)} />
            )}
            {presetPreviewLabel && (
              <p className="text-xs text-muted-foreground mt-2">Will fire at: {presetPreviewLabel} ({userTimezone})</p>
            )}
            {preset === "custom" && customDateTime && (
              <p className="text-xs text-muted-foreground mt-2">Will fire at: {format(new Date(customDateTime), "EEE MMM d, h:mm a")} ({userTimezone})</p>
            )}
          </div>

          <div>
            <Label>Note (optional)</Label>
            <Textarea value={note} onChange={e => setNote(e.target.value)} placeholder="" rows={2} />
          </div>

          <div>
            <Label>Assign to</Label>
            <Select value={assignedToId ?? "me"} onValueChange={v => setAssignedToId(v === "me" ? undefined : v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="me">Me</SelectItem>
                {(usersQ.data ?? []).map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Repeat</Label>
            <Select value={recurrenceRule} onValueChange={v => setRecurrenceRule(v as RecurrenceRule | "none")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Don't repeat</SelectItem>
                <SelectItem value="daily">Every day</SelectItem>
                <SelectItem value="weekdays">Every weekday</SelectItem>
                <SelectItem value="weekly">Every week</SelectItem>
                <SelectItem value="biweekly">Every 2 weeks</SelectItem>
                <SelectItem value="monthly">Every month</SelectItem>
                <SelectItem value="yearly">Every year</SelectItem>
              </SelectContent>
            </Select>
            {recurrenceRule !== "none" && (
              <div className="mt-2">
                <Label className="text-xs text-muted-foreground">End date (optional)</Label>
                <Input type="date" value={recurrenceEndDate} onChange={e => setRecurrenceEndDate(e.target.value)} />
              </div>
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            This shows up in your tasks as well. A reminder is a task with an alarm on it.
          </p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting || !title.trim()}>{submitting ? "Setting…" : "Set reminder"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
